/**
 * SessionStorage stash for a save-search intent interrupted by the login
 * bounce. The marketplace has no in-page login modal, so a logged-out "Save
 * search" tap hard-navigates to `/login?redirectTo=...`; without this the
 * user's intent is silently dropped on return. The claim is destructive and
 * TTL-guarded so a stale stash from an abandoned login can't reopen a save
 * dialog days later.
 */
import type { PropertySearchQuery } from '../helpers/useSearchQueries';

const STORAGE_KEY = 'opendoor.cosmos.pendingSavedSearch';
const TTL_MS = 30 * 60 * 1000;

export interface PendingSavedSearch {
  name: string;
  query: PropertySearchQuery;
}

interface StoredPendingSavedSearch extends PendingSavedSearch {
  stashedAt: number;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNullableString = (value: unknown): value is string | null | undefined =>
  value == null || typeof value === 'string';

const isNullableNumber = (value: unknown): value is number | null | undefined =>
  value == null || typeof value === 'number';

const isNullableBoolean = (value: unknown): value is boolean | null | undefined =>
  value == null || typeof value === 'boolean';

const parseStringArray = (value: unknown): string[] | null | undefined => {
  if (value == null) {
    return value;
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  const strings: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') {
      return undefined;
    }
    strings.push(item);
  }
  return strings;
};

const parseVertices = (value: unknown): PropertySearchQuery['vertices'] | undefined => {
  if (value == null) {
    return value;
  }
  if (!Array.isArray(value)) {
    return undefined;
  }
  const vertices: NonNullable<PropertySearchQuery['vertices']> = [];
  for (const point of value) {
    if (!isPlainObject(point) || typeof point.lat !== 'number' || typeof point.lon !== 'number') {
      return undefined;
    }
    vertices.push({ lat: point.lat, lon: point.lon });
  }
  return vertices;
};

const sanitizeQuery = (query: unknown): PropertySearchQuery | null => {
  if (!isPlainObject(query)) {
    return null;
  }

  const sanitized: PropertySearchQuery = {};
  for (const [key, value] of Object.entries(query)) {
    switch (key) {
      case 'market':
      case 'preview_aid':
        if (!isNullableString(value)) {
          return null;
        }
        sanitized[key] = value;
        break;
      case 'zoneIds': {
        const zoneIds = parseStringArray(value);
        if (zoneIds === undefined) {
          return null;
        }
        sanitized.zoneIds = zoneIds;
        break;
      }
      case 'vertices': {
        const vertices = parseVertices(value);
        if (vertices === undefined) {
          return null;
        }
        sanitized.vertices = vertices;
        break;
      }
      case 'bedrooms':
      case 'maxBedrooms':
      case 'bathrooms':
      case 'minListPrice':
      case 'maxListPrice':
      case 'minSquareFootage':
      case 'maxSquareFootage':
      case 'minYearBuilt':
      case 'maxYearBuilt':
      case 'minLotSize':
      case 'maxLotSize':
        if (!isNullableNumber(value)) {
          return null;
        }
        sanitized[key] = value;
        break;
      case 'canSelfTour':
      case 'availableSoon':
      case 'includeAvailableSoon':
      case 'singleFamily':
      case 'condo':
      case 'townhome':
      case 'hasSingleStory':
      case 'hasPool':
      case 'noHoas':
        if (!isNullableBoolean(value)) {
          return null;
        }
        sanitized[key] = value;
        break;
      default:
        break;
    }
  }
  return sanitized;
};

export const stashPendingSavedSearch = (pending: PendingSavedSearch): void => {
  try {
    const stored: StoredPendingSavedSearch = { ...pending, stashedAt: Date.now() };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable (private mode/quota): the save just doesn't resume.
  }
};

/** Reads and removes the pending save. Returns null if absent or expired. */
export const claimPendingSavedSearch = (): PendingSavedSearch | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    window.sessionStorage.removeItem(STORAGE_KEY);
    const stored: unknown = JSON.parse(raw);
    if (!isPlainObject(stored)) {
      return null;
    }
    const query = sanitizeQuery(stored.query);
    if (
      typeof stored.name !== 'string' ||
      typeof stored.stashedAt !== 'number' ||
      !query ||
      Date.now() - stored.stashedAt > TTL_MS
    ) {
      return null;
    }
    return { name: stored.name, query };
  } catch {
    return null;
  }
};
